import { ActionTree, GetterTree, MutationTree } from "vuex";
import axios, { AxiosResponse } from "axios";
import { Category } from "@/models/help-center/Category";
import { Entry } from "@/models/help-center/Entry";
import { HelpCenterState } from "@/models/store/HelpCenterState";
import { Pagination } from "@/models/help-center/Pagination";
import { RootState } from "@/models/store/RootState";
import _ from "lodash";

export const helpCenterOriginalState = {
  loadingState: [],
  entries: {
    list: [],
    pagination: {},
    current: {
      id: null,
      help_center_category_id: null,
      title: null,
      content: null
    }
  },
  categories: {
    list: [],
    pagination: {},
    current: {
      id: null,
      name: null,
      description: null,
      slug: null
    }
  }
};

export const helpCenterActions: ActionTree<HelpCenterState, RootState> = {
  getAllCategories: ({ commit, dispatch }) => {
    commit("addToLoadingState", "getting all help center categories");
    return axios.get(`/help-center/categories`)
      .then((resp: AxiosResponse) => {
        commit("setCategoriesList", resp.data);
        commit("removeFromLoadingState", "getting all help center categories");
      }).catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting all help center categories");
        throw new Error(err);
      });
  },
  getCategory: ({ commit, dispatch }, categoryId: number) => {
    commit("addToLoadingState", "getting individual help center category");
    return axios.get(`/help-center/categories/${categoryId}`)
      .then((resp: AxiosResponse) => {
        commit("setCurrentCategory", resp.data);
        commit("removeFromLoadingState", "getting individual help center category");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting individual help center category");
        throw new Error(err);
      });
  },
  createCategory: ({ commit, dispatch }, category: Category) => {
    commit("addToLoadingState", "creating help center category");
    return axios.post(`/help-center/categories`, { ...category })
      .then(() => {
        dispatch(
          "snackbar/successMessage",
          `Help Center Category successfully created.`,
          { root: true }
        );
        commit("removeFromLoadingState", "creating help center category");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "creating help center category");
        throw new Error(err);
      });
  },
  updateCategory: ({ commit, dispatch }, category: Category) => {
    commit("addToLoadingState", "updating help center category");
    return axios.patch(`/help-center/categories/${category.id}`, {
      description: category.description,
      name: category.name,
      slug: category.slug
    })
      .then(() => {
        dispatch(
          "snackbar/successMessage",
          `Help Center Category successfully updated.`,
          { root: true }
        );
        commit("removeFromLoadingState", "updating help center category");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "updating help center category");
        throw new Error(err);
      });
  },
  deleteCategory: ({ commit, dispatch }, categoryId: number) => {
    commit("addToLoadingState", "deleting help center category");
    return axios.delete(`/help-center/categories/${categoryId}`)
      .then(() => {
        dispatch(
          "snackbar/successMessage",
          `Help Center Category successfully deleted.`,
          { root: true }
        );
        commit("removeFromLoadingState", "deleting help center category");
        dispatch("getAllCategories");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "deleting help center category");
        throw new Error(err);
      });
  },

  getAllEntries: ({ commit, dispatch }) => {
    commit("addToLoadingState", "getting all help center entries");
    return axios.get(`/help-center/entries`)
      .then((resp: AxiosResponse) => {
        commit("setEntriesList", resp.data);
        commit("removeFromLoadingState", "getting all help center entries");
      }).catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting all help center entries");
        throw new Error(err);
      });
  },
  getIndividualEntry: ({ commit, dispatch }, entryId: number) => {
    commit("addToLoadingState", "getting individual help center entry");
    return axios.get(`/help-center/entries/${entryId}`)
      .then((resp: AxiosResponse) => {
        commit("setCurrentEntry", resp.data);
        commit("removeFromLoadingState", "getting individual help center entry");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting individual help center entry");
        throw new Error(err);
      });
  },
  createEntry: ({ commit, dispatch }, entry: Entry) => {
    commit("addToLoadingState", "creating help center entry");
    return axios.post(`/help-center/entries`, entry)
      .then(() => {
        dispatch("getAllEntries");
        dispatch(
          "snackbar/successMessage",
          `Help Center Entry successfully created.`,
          { root: true }
        );
        commit("removeFromLoadingState", "creating help center entry");
        commit("resetCurrentEntry");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "creating help center entry");
        throw new Error(err);
      });
  },
  updateEntry: ({ commit, dispatch }, entry: Entry) => {
    commit("addToLoadingState", "updating help center entry");
    return axios.patch(`/help-center/entries/${entry.id}`, entry)
      .then(() => {
        dispatch(
          "snackbar/successMessage",
          `Help Center Entry successfully updated.`,
          { root: true }
        );
        dispatch("getAllEntries");
        commit("resetCurrentEntry");
        commit("removeFromLoadingState", "updating help center entry");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "updating help center entry");
        throw new Error(err);
      });
  },
  deleteEntry: ({ commit, dispatch }, entryId: number) => {
    commit("addToLoadingState", "deleting help center entry");
    return axios.delete(`/help-center/entries/${entryId}`)
      .then(() => {
        dispatch(
          "snackbar/successMessage",
          `Help Center Entry successfully deleted.`,
          { root: true }
        );
        dispatch("getAllEntries");
        commit("removeFromLoadingState", "deleting help center entry");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "deleting help center entry");
        throw new Error(err);
      });
  },
  handleResetCurrentEntry: ({ commit }) => {
    commit("resetCurrentEntry");
  },
  handleSetEntryPagination: ({ commit }, payload: Pagination) => {
    commit("setEntriesPagination", payload);
  }
};

export const helpCenterMutations: MutationTree<HelpCenterState> = {
  addToLoadingState: (state: HelpCenterState, message: string) => {
    state.loadingState.push(message);
  },
  removeFromLoadingState: (state: HelpCenterState, message: string) => {
    state.loadingState = state.loadingState.filter(loadingMessage => loadingMessage !== message);
  },
  setCategoriesList: (state: HelpCenterState, payload: Category[]) => {
    state.categories.list = payload;
  },
  setCategoriesPagination: (state: HelpCenterState, payload: Pagination) => {
    state.categories.pagination = payload;
  },
  setEntriesList: (state: HelpCenterState, payload: Entry[]) => {
    state.entries.list = payload;
  },
  setEntriesPagination: (state: HelpCenterState, payload: Pagination) => {
    state.entries.pagination = payload;
  },
  setCurrentCategory: (state: HelpCenterState, payload) => {
    state.categories.current = payload;
  },
  resetCurrentCategory: (state: HelpCenterState) => {
    state.categories.current = _.cloneDeep(helpCenterOriginalState.categories.current);
  },
  resetCurrentEntry: (state: HelpCenterState) => {
    state.entries.current = _.cloneDeep(helpCenterOriginalState.entries.current);
  },
  setCurrentEntry: (state: HelpCenterState, payload) => {
    state.entries.current = payload;
  },
  resetEntryCategory: (state: HelpCenterState) => {
    state.entries.current = _.cloneDeep(helpCenterOriginalState.entries.current);
  }
};

export const helpCenterGetters: GetterTree<HelpCenterState, RootState> = {
  categoriesList: (state: HelpCenterState) => {
    return state.categories.list;
  },
  categoriesPagination: (state: HelpCenterState) => {
    return state.categories.pagination;
  },
  currentCategory: (state: HelpCenterState) => {
    return state.categories.current;
  },
  currentEntry: (state: HelpCenterState) => {
    return state.entries.current;
  },
  error: (state: HelpCenterState) => {
    return state.error;
  },
  loadingState: (state: HelpCenterState) => {
    return state.loadingState;
  },
  success: (state: HelpCenterState) => {
    return state.success;
  }
};

export default {
  name: "helpCenter",
  namespaced: true,
  state: _.cloneDeep(helpCenterOriginalState),
  actions: helpCenterActions,
  mutations: helpCenterMutations,
  getters: helpCenterGetters
};
